function includesAny(text, terms) {
  return terms.some(term => text.includes(term));
}

function buildSearchWithParams(baseSearch = '', entries = []) {
  const params = new URLSearchParams(String(baseSearch || '').replace(/^\?/, ''));
  entries.forEach(([key, value]) => {
    if (value == null || value === '') return;
    params.set(key, value);
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

const AGENT_META = {
  CHAT_AGENT: {
    agent: 'Chat Agent',
    title: 'Stay in Chat',
    desc: 'This work looks like drafting, synthesis, or direct application to your facts.',
    path: '/chat',
  },
  RESEARCH_AGENT: {
    agent: 'Research Agent',
    title: 'Move this into Research',
    desc: 'This looks authority-heavy and should stay grounded in verified Indian legal sources and matter context.',
    path: '/research',
  },
  SABHA: {
    agent: 'Sabha',
    title: 'Move this into Sabha',
    desc: 'This is better handled as a multi-lens matter exercise with courtroom, public, and strategic pressure-testing.',
    path: '/war-room',
  },
  AINA: {
    agent: 'Aina',
    title: 'Move this into Aina',
    desc: 'This needs adversarial stress-testing, bench-pressure questions, or weakness finding.',
    path: '/aina',
  },
  JOLLY: {
    agent: 'Jolly',
    title: 'Open Jolly for voice',
    desc: 'This is better captured as dictation, transcript review, or a spoken debrief.',
    path: '/jolly',
  },
};

export function buildAgentHandoffRecommendation({
  currentAgent = 'CHAT_AGENT',
  workspace = null,
  userPrompt = '',
  assistantResponse = '',
}) {
  const text = `${userPrompt}\n${assistantResponse}`.toLowerCase();
  const workspaceAgent = String(workspace?.recommended_agent || '').toUpperCase();
  const workspaceConfidence = String(workspace?.activity_confidence || '').toUpperCase();
  const activityType = String(workspace?.activity_type || '').toUpperCase();

  if (currentAgent === 'CHAT_AGENT') {
    if (includesAny(text, ['transcribe', 'transcription', 'dictate', 'voice note', 'spoken note', 'debrief the hearing', 'record this', 'what was said'])) {
      return { ...AGENT_META.JOLLY, target: 'JOLLY', confidence: 'HIGH', reason: 'The request is voice-first and belongs in Jolly.' };
    }
    if (includesAny(text, ['red team', 'opposing counsel', 'bench question', 'find weakness', 'weaknesses', 'attack my', 'challenge my', 'what can go wrong'])) {
      return { ...AGENT_META.AINA, target: 'AINA', confidence: 'HIGH', reason: 'The next step is adversarial pressure-testing.' };
    }
    if (includesAny(text, ['bearish', 'bullish', 'political impact', 'public opinion', 'media reaction', 'how will this play out', 'judicial outcome', 'scenario'])) {
      return { ...AGENT_META.SABHA, target: 'SABHA', confidence: 'HIGH', reason: 'The next step is scenario planning across Sabha lenses.' };
    }
    if (workspaceAgent === 'RESEARCH_AGENT' && workspaceConfidence === 'HIGH') {
      return {
        ...AGENT_META.RESEARCH_AGENT,
        target: 'RESEARCH_AGENT',
        confidence: 'HIGH',
        reason: activityType
          ? `The shared workspace is tracking this as ${activityType.replace(/_/g, ' ').toLowerCase()}.`
          : 'The shared workspace expects the next step to be authority-first research.',
      };
    }
  }

  if (currentAgent === 'RESEARCH_AGENT' && workspaceAgent === 'CHAT_AGENT' && workspaceConfidence === 'HIGH') {
    return {
      ...AGENT_META.CHAT_AGENT,
      target: 'CHAT_AGENT',
      confidence: 'HIGH',
      reason: activityType
        ? `The shared workspace now looks like ${activityType.replace(/_/g, ' ').toLowerCase()} work.`
        : 'The authority set is ready for drafting or synthesis in Chat.',
    };
  }

  return null;
}

export function buildAgentDestination({
  recommendation,
  workspaceQueryString = '',
  workspace = null,
  matterId = '',
  matterName = '',
  prefillQuery = '',
  prefillType = '',
  focusAuthority = '',
  focusIssue = '',
  journeyKey = '',
  chatId = '',
  chatReturnPath = '',
}) {
  if (!recommendation) return { href: '/chat', state: undefined };

  const target = String(recommendation.target || '').toUpperCase();
  if (target === 'RESEARCH_AGENT' || target === 'CHAT_AGENT') {
    return {
      href: `${recommendation.path}${buildSearchWithParams(workspaceQueryString, [
        ['prefill_query', prefillQuery],
        ['prefill_type', prefillType],
        ['focus_authority', focusAuthority],
        ['focus_issue', focusIssue],
        ['journey_key', journeyKey],
        ['chat_id', chatId],
        ['chat_return_path', chatReturnPath],
      ])}`,
      state: undefined,
    };
  }

  if (target === 'AINA') {
    const path = matterId ? `/aina/${matterId}` : (recommendation.path || '/aina');
    return {
      href: `${path}${buildSearchWithParams(workspaceQueryString, [
        ['matter_id', matterId],
        ['matter_name', matterName],
        ['prefill_prompt', prefillQuery],
        ['open_chat', prefillQuery ? '1' : ''],
        ['journey_key', journeyKey],
        ['chat_id', chatId],
        ['chat_return_path', chatReturnPath],
      ])}`,
      state: undefined,
    };
  }

  if (target === 'VAKALAT') {
    const masterDocumentId = workspace?.master_document_id;
    return {
      href: masterDocumentId ? `/documents/${masterDocumentId}` : '/documents',
      state: workspace?.workspace_id
        ? {
            workspaceId: workspace.workspace_id,
            journeyKey,
            chatId,
            chatReturnPath,
          }
        : undefined,
    };
  }

  if (target === 'SABHA') {
    const path = matterId ? `/war-room/${matterId}` : (recommendation.path || '/war-room');
    return {
      href: `${path}${buildSearchWithParams(workspaceQueryString, [
        ['view', recommendation.routeHint === 'bench' ? 'bench' : 'overview'],
        ['matter_id', matterId],
        ['matter_name', matterName],
        ['journey_key', journeyKey],
        ['chat_id', chatId],
        ['chat_return_path', chatReturnPath],
      ])}`,
      state: undefined,
    };
  }

  if (target === 'JOLLY') {
    return {
      href: `${recommendation.path || '/jolly'}${buildSearchWithParams(workspaceQueryString, [
        ['matter_id', matterId],
        ['matter_name', matterName],
        ['prefill_query', prefillQuery],
        ['journey_key', journeyKey],
        ['chat_id', chatId],
        ['chat_return_path', chatReturnPath],
      ])}`,
      state: undefined,
    };
  }

  return {
    href: `${recommendation.path}${buildSearchWithParams(workspaceQueryString, [
      ['matter_id', matterId],
      ['matter_name', matterName],
      ['journey_key', journeyKey],
      ['chat_id', chatId],
      ['chat_return_path', chatReturnPath],
    ])}`,
    state: undefined,
  };
}
